<template>
  <BaseModalEditCreate
    mode="add"
    :is-modified="false"
    :close-on-outside-click="false"
    max-width="extra"
    @dismiss-changes="emit('close')"
    @save="emitSelectedRows = true">
    <template #form>
      <div class="overflow-hidden h-full grid grid-rows-[auto_1fr]">
        <div class="flex w-full justify-between items-center mb-1">
          <p class="font-light">{{ $t('messages.info.ctrlClickToAddItems') }}</p>
          <button
            type="button"
            @click="isAddFilteredModalActive = true"
            class="px-2 py-1 font-normal border border-gray-300 rounded-md">
            <span class="flex items-center justify-center gap-2 text-sm capitalize text-theme-500">
              <IconAddListOutline class="size-4" />

              <p>
                {{ tableQuery ? $t('common.actions.addFiltered') : $t('common.actions.addAll') }}
              </p>
            </span>
          </button>
        </div>
        <div class="w-full h-full overflow-x-hidden">
          <BaseTable
            v-if="list"
            table-id="price-catalog-details-materials-table"
            :total-records="totalRecords"
            :headers="materialHeaders"
            :list="list"
            :emit-selected-rows="emitSelectedRows"
            only-search
            hide-multiselect-delete
            hide-multiselect-download
            @refresh-data="onRefreshData"
            @selected-rows="onMultiselect" />
        </div>
      </div>
    </template>
    <template #saveButtonText> {{ $t('common.actions.add') }} </template>
  </BaseModalEditCreate>
  <BaseModalConfirm
    v-if="isAddFilteredModalActive"
    @cancel="isAddFilteredModalActive = false"
    @confirm="onAddAllFilteredMaterials">
    <template #title>{{ $t('common.actions.addAll') }}</template>
    <template #text>
      <div class="flex flex-col w-full gap-6">
        <p>{{ $t('price-catalog.addAllModalText') }}</p>
        <div class="flex flex-col items-start justify-start gap-4">
          <div class="flex justify-between w-full text-base font-light tracking-wide">
            {{ $t('pricing-catalogs.price') }}
            <div>
              <BaseInput type="number" color="base" :min="0" flat size="sm" v-model="addAllPrice" />
            </div>
          </div>
          <BaseToggle
            class="text-sm"
            :label="$t('price-catalog.permitDiscount')"
            name="allow-discounts"
            light
            v-model="addAllPermitDiscount" />
          <BaseToggle
            class="flex justify-center text-sm"
            :label="$t('price-catalog.includeVat')"
            name="include-vat"
            light
            disable
            v-model="isHeaderIncludedVat" />
          <p>{{ $t('price-catalog.addAllModalSubText') }}</p>
        </div>
      </div>
    </template>
    <template #buttonText>{{ $t('common.actions.create') }}</template>
  </BaseModalConfirm>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseTable from '../common/BaseTable.vue';
import type { Material } from '@/types/Materials';
import type { TableFilterData } from '@/types/Base';
import { useI18n } from 'vue-i18n';
import IconAddListOutline from '@/assets/icons/IconAddListOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseToggle from '../common/BaseToggle.vue';

const props = defineProps<{
  list: Material[];
  totalRecords?: number;
  isAddAllButtonActive?: boolean;
  headerIncludeVat: boolean;
}>();

const emit = defineEmits(['close', 'refreshData', 'multiSelect', 'addAllFiltered']);

const { t: $t } = useI18n({ useScope: 'global' });
const materialHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'smallCode', text: $t('material.smallCode'), type: 'text', active: true },
  { value: 'smallName', text: $t('material.smallName'), type: 'text', active: true },
  { value: 'sequence', text: $t('material.sequence'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
];

const isAddFilteredModalActive = ref(false);
const addAllPrice = ref(0.01);
const addAllPermitDiscount = ref(true);
const isHeaderIncludedVat = ref(props.headerIncludeVat);
const tableQuery = ref<string>();

function onRefreshData(data: TableFilterData) {
  data.sortColumn = data.column ? materialHeaders[data.column].value : '';
  tableQuery.value = data.query;
  emit('refreshData', data);
}

const emitSelectedRows = ref(false);
function onMultiselect(items: Material[]) {
  emit('multiSelect', items);
}

function onAddAllFilteredMaterials() {
  isAddFilteredModalActive.value = false;
  emit('addAllFiltered', addAllPrice.value, addAllPermitDiscount.value, tableQuery.value);
}
</script>
